import { watch, ref } from 'vue'

export type PositionedArrayItem = {
  id?: string
  position: number
  _destroy?: boolean
}

export function usePositionedArray<T extends PositionedArrayItem>(array: T[], idKey: keyof T) {
  const destroyedCount = ref(0)

  function sortArray() {
    array.sort((a, b) => a.position - b.position)
  }

  function create(item: Omit<T, 'position'>) {
    const newItem = { ...item, position: array.length } as T
    array.push(newItem)
    updatePositions()
    return newItem
  }

  function destroy(index: number) {
    if (array[index].id) {
      array[index]._destroy = true
      array[index].position = -1
      sortArray()
      destroyedCount.value = destroyedCount.value + 1
    } else {
      array.splice(index, 1)
    }
    updatePositions()
  }

  function moveUp(index: number) {
    if (index > 0) {
      const [removed] = array.splice(index, 1)
      array.splice(index - 1, 0, removed)
      updatePositions()
    }
  }

  function moveDown(index: number) {
    if (index < array.length - 1) {
      const [removed] = array.splice(index, 1)
      array.splice(index + 1, 0, removed)
      updatePositions()
    }
  }

  function updatePositions() {
    let index = 0
    array.forEach((item) => {
      if (item._destroy) {
        item.position = -1
      } else {
        item.position = index
        index++
      }
    })
  }

  function cleanupDuplicates() {
    const newElements = array.filter((item) => !item.id)
    const indexToDrop: number[] = []
    newElements.forEach((newItem) => {
      const deletedItemIndex = array.findIndex((item) => item._destroy && item[idKey] === newItem[idKey])

      if (deletedItemIndex > -1) {
        newItem.id = array[deletedItemIndex].id
        indexToDrop.push(deletedItemIndex)
      }
    })
    indexToDrop.forEach((index) => {
      array.splice(index, 1)
    })

    sortArray()
    destroyedCount.value = array.filter((item) => item._destroy).length
  }

  watch(array, () => {
    if (destroyedCount.value) {
      cleanupDuplicates()
    }
  })

  return { create, destroy, moveDown, moveUp }
}
